import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom';
import gsap from 'gsap';

type Phase = 'idle' | 'covering' | 'waiting' | 'revealing';
type Push = (to: unknown, state?: unknown, opts?: unknown) => void;

const COVER = 0.35;
const REVEAL = 0.4;
const EASE = 'power2.inOut';

function pathOf(to: unknown) {
  if (typeof to === 'string') {
    const u = new URL(to, window.location.href);
    return { pathname: u.pathname, search: u.search };
  }
  const p = (to ?? {}) as { pathname?: string; search?: string };
  return { pathname: p.pathname ?? window.location.pathname, search: p.search ?? '' };
}

/**
 * Single app-shell curtain. Wraps the router navigator so EVERY push (Link or navigate())
 * is delayed until the curtain has fully risen; then the route changes behind it and the
 * curtain keeps moving up and exits through the top. Back/forward (POP), replace,
 * same-page anchors and direct loads are untouched.
 */
export default function RouteTransition({ children }: { children: ReactNode }) {
  const ctx = useContext(UNSAFE_NavigationContext);
  const location = useLocation();
  const curtain = useRef<HTMLDivElement>(null);
  const phase = useRef<Phase>('idle');
  const pending = useRef<(() => void) | null>(null);
  const queued = useRef<(() => void) | null>(null);
  const awaiting = useRef(false);

  useEffect(() => {
    if (curtain.current) gsap.set(curtain.current, { x: 0, y: 0, yPercent: 100 });
  }, []);

  const startRef = useRef<(go: () => void) => void>(() => {});

  const reveal = useCallback(() => {
    const el = curtain.current;
    if (!el) return;
    phase.current = 'revealing';
    gsap.to(el, {
      yPercent: -100,
      duration: REVEAL,
      ease: EASE,
      onComplete: () => {
        gsap.set(el, { visibility: 'hidden', pointerEvents: 'none', yPercent: 100 });
        phase.current = 'idle';
        const main = document.querySelector<HTMLElement>('main');
        if (main) {
          if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
          main.focus({ preventScroll: true });
        }
        const next = queued.current;
        queued.current = null;
        if (next) startRef.current(next);
      },
    });
  }, []);

  const start = useCallback((go: () => void) => {
    const el = curtain.current;
    if (!el) { go(); return; }
    phase.current = 'covering';
    pending.current = go;
    gsap.killTweensOf(el);
    gsap.set(el, { y: 0, yPercent: 100, visibility: 'visible', pointerEvents: 'auto' });
    gsap.to(el, {
      yPercent: 0,
      duration: COVER,
      ease: EASE,
      onComplete: () => {
        const fn = pending.current;
        pending.current = null;
        phase.current = 'waiting';
        awaiting.current = true;
        fn?.();
      },
    });
  }, []);
  startRef.current = start;

  // Destination committed: wait for the page's ScrollTriggers + shared refresh, then reveal.
  useEffect(() => {
    if (phase.current !== 'waiting' || !awaiting.current) return undefined;
    awaiting.current = false;
    let frames = location.hash ? 4 : 2;
    let raf = 0;
    const tick = () => {
      frames -= 1;
      if (frames <= 0) { reveal(); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [location.key, location.hash, reveal]);

  const value = useMemo(() => {
    const nav = ctx.navigator as unknown as { push: Push };
    const push: Push = (to, state, opts) => {
      const { pathname, search } = pathOf(to);
      const same = pathname === window.location.pathname && search === window.location.search;
      const go = () => nav.push(to, state, opts);
      if (same) { go(); return; }
      if (phase.current === 'covering') { pending.current = go; return; }
      if (phase.current !== 'idle') { queued.current = go; return; }
      start(go);
    };
    return { ...ctx, navigator: { ...ctx.navigator, push } };
  }, [ctx, start]);

  return (
    <UNSAFE_NavigationContext.Provider value={value as typeof ctx}>
      {children}
      <div
        ref={curtain}
        aria-hidden="true"
        className="fixed inset-0 z-[9999] bg-[#101114] border-t-2 border-[#7C6AD6] will-change-transform"
        style={{ visibility: 'hidden', pointerEvents: 'none' }}
      />
    </UNSAFE_NavigationContext.Provider>
  );
}